import React, { useEffect, useState, useRef } from 'react';
import { array, bool, func, number, object, string } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Form as FinalForm, FormSpy } from 'react-final-form';
import { FORM_ERROR } from 'final-form';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';
import moment from 'moment';
import omit from 'lodash/omit';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faMinus, faXmark } from '@fortawesome/free-solid-svg-icons';
import { types as sdkTypes } from '../../../util/sdkLoader';
import ReactMarkdown from 'react-markdown';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import {
  required,
  bookingDatesRequired,
  composeValidators,
  numberAtLeast,
  minimalRentalPeriodRequired,
} from '../../../util/validators';
import {
  START_DATE,
  END_DATE,
  getStartOf,
  addTime,
  isSameDay,
  isDateSameOrAfter,
  isInRange,
  timeOfDayFromLocalToTimeZone,
  timeOfDayFromTimeZoneToLocal,
  monthIdString,
  initialVisibleMonth,
  diffInTime,
} from '../../../util/dates';
import { LINE_ITEM_DAY, LINE_ITEM_NIGHT, TIME_SLOT_TIME, propTypes } from '../../../util/types';
import { BOOKING_PROCESS_NAME, getProcess } from '../../../transactions/transaction';
import { CART_ADD, useUpdateCartMutation } from '../../../ducks/CartApi';
import { manageDisableScrolling } from '../../../ducks/ui.duck';
import {
  getSufficientSeats,
  getAvailabilityForDayWithBuffer,
  createDayToSeats,
} from '../../../util/data';
import configDefault from '../../../config/configDefault';
import { parse, stringify } from '../../../util/urlHelpers';

import {
  Form,
  IconArrowHead,
  PrimaryButton,
  FieldDateRangeInput,
  H6,
  Modal,
  SecondaryButton,
  NamedLink,
  FieldTextInput,
  FieldRadioButton,
  IconSpinner,
  H4,
} from '../../../components';

import EstimatedCustomerBreakdownMaybe, {
  estimatedCustomerTransaction,
} from '../EstimatedCustomerBreakdownMaybe';
import FieldDeliveryAddress from './FieldDeliveryAddress';

import css from './BookingDatesForm.module.css';
import LineItemBasePriceMaybe from '../../OrderBreakdown/LineItemBasePriceMaybe';
import LineItemShippingFeeMaybe from '../../OrderBreakdown/LineItemShippingFeeMaybe';
import LineItemTotalPrice from '../../OrderBreakdown/LineItemTotalPrice';
import LineItemSubTotalMaybe from '../../OrderBreakdown/LineItemSubTotalMaybe';
import BookingDeliveryRadioButtonComponent from './BookingDeliveryRadioButton';
import { formatMoney } from '../../../util/currency';
import { fallbackSections } from '../../../containers/TermsOfServicePage/FallbackPage';
const { Money } = sdkTypes;

const TODAY = new Date();

const nextMonthFn = (currentMoment, timeZone, offset = 1) =>
  getStartOf(currentMoment, 'month', timeZone, offset, 'months');
const prevMonthFn = (currentMoment, timeZone, offset = 1) =>
  getStartOf(currentMoment, 'month', timeZone, -1 * offset, 'months');
const endOfRange = (date, dayCountAvailableForBooking, timeZone) =>
  getStartOf(date, 'day', timeZone, dayCountAvailableForBooking - 1, 'days');

/**
 * Return a boolean indicating if given date can be found in an array
 * of tile slots (start dates).
 */
const timeSlotsContain = (timeSlots, date, timeZone) => {
  const foundIndex = timeSlots.findIndex(slot =>
    isInRange(date, slot.attributes.start, slot.attributes.end, 'hour', timeZone)
  );
  return foundIndex > -1;
};

const pickMonthlyTimeSlots = (monthlyTimeSlots, date, timeZone) => {
  const monthId = monthIdString(date, timeZone);
  return monthlyTimeSlots?.[monthId]?.timeSlots || [];
};

const parseSearchCountry = ({ search }) => {
  const { "pub_country": countriesParam } = parse(search);
  return {
    "has_all:DE": "DE",
    "has_all:AT": "AT",
    "has_all:CH": "CH",
    "has_all:IT": "IT"
  }[countriesParam] || null;
};

/**
 * Find first blocked date between two dates.
 * If none is found, null is returned.
 *
 * @param {Object} monthlyTimeSlots propTypes.timeSlot objects
 * @param {Moment} startDate start date, exclusive
 * @param {Moment} endDate end date, exclusive
 * @param {String} timeZone time zone id
 */
const firstBlockedBetween = (monthlyTimeSlots, startDate, endDate, timeZone) => {
  const firstDate = getStartOf(startDate, 'day', timeZone, 1, 'days');
  if (isSameDay(firstDate, endDate, timeZone)) {
    return null;
  }

  const timeSlots = pickMonthlyTimeSlots(monthlyTimeSlots, firstDate, timeZone);
  const contains = timeSlotsContain(timeSlots, firstDate, timeZone);
  return contains ? firstBlockedBetween(monthlyTimeSlots, firstDate, endDate, timeZone) : firstDate;
};

/**
 * Find last blocked date between two dates.
 * If none is found, null is returned.
 *
 * @param {Array} timeSlots propTypes.timeSlot objects
 * @param {Moment} startDate start date, exclusive
 * @param {Moment} endDate end date, exclusive
 * @param {String} timeZone time zone id
 */
const lastBlockedBetween = (monthlyTimeSlots, startDate, endDate, timeZone) => {
  const previousDate = getStartOf(endDate, 'day', timeZone, -1, 'days');
  if (isSameDay(previousDate, startDate, timeZone)) {
    return null;
  }

  const timeSlots = pickMonthlyTimeSlots(monthlyTimeSlots, previousDate, timeZone);
  return timeSlotsContain(timeSlots, previousDate, timeZone)
    ? lastBlockedBetween(monthlyTimeSlots, startDate, previousDate, timeZone)
    : previousDate;
};

/**
 * Check if a blocked date can be found between two dates.
 *
 * @param {Object} timeSlots propTypes.timeSlot objects
 * @param {String} timeZone time zone id
 * @param {Moment} startDate start date, exclusive
 * @param {Moment} endDate end date, exclusive
 */
const isBlockedBetween = (monthlyTimeSlots, timeZone) => (startDate, endDate) => {
  const startInListingTZ = getStartOf(
    timeOfDayFromLocalToTimeZone(startDate, timeZone),
    'day',
    timeZone
  );
  const endInListingTZ = getStartOf(
    timeOfDayFromLocalToTimeZone(endDate, timeZone),
    'day',
    timeZone
  );
  return !!firstBlockedBetween(monthlyTimeSlots, startInListingTZ, endInListingTZ, timeZone);
};

const isStartDateSelected = (timeSlots, startDate, endDate, focusedInput) => {
  return (
    timeSlots && startDate && (!endDate || focusedInput === END_DATE) && focusedInput !== START_DATE
  );
};

const endDateToPickerDate = (unitType, endDate, timeZone) => {
  const isValid = endDate instanceof Date;
  const isDaily = unitType === LINE_ITEM_DAY;

  if (!isValid) {
    return null;
  } else if (isDaily) {
    // API end dates are exlusive, so we need to shift them with daily
    // booking.
    return getStartOf(endDate, 'day', timeZone, -1, 'days');
  } else {
    return endDate;
  }
};

/**
 * Returns an isOutsideRange function that can be passed to
 * a react-dates DateRangePicker component.
 */
const isOutsideRangeFn = ({
  monthlyTimeSlots,
  startDate,
  endDate,
  lineItemUnitType,
  dayCountAvailableForBooking,
  timeZone,
  listing,
}) => focusedInput => {
  const endOfAvailableRange = dayCountAvailableForBooking - 1;
  const endOfAvailableRangeDate = getStartOf(TODAY, 'day', timeZone, endOfAvailableRange, 'days');
  const outOfBookableDate = getStartOf(TODAY, 'day', timeZone, endOfAvailableRange + 1, 'days');
  const startOfStartDay = getStartOf(startDate, 'day', timeZone);
  const { endBuffer } = listing?.attributes?.publicData || {};

  // start date selected, end date missing
  const startDateSelected = isStartDateSelected(monthlyTimeSlots, startDate, endDate, focusedInput);
  const endOfBookableRangeRaw = startDateSelected
    ? firstBlockedBetween(monthlyTimeSlots, startDate, outOfBookableDate, timeZone)
    : null;

  if (endOfBookableRangeRaw) {
    const endOfBookableRange = moment(endOfBookableRangeRaw)
      .subtract(Number.parseInt(endBuffer), 'days')
      .toDate();
    // end the range so that the booking can end at latest on
    // nightly booking: the day the next booking starts
    // daily booking: the day before the next booking starts
    return day => {
      const timeOfDay = timeOfDayFromLocalToTimeZone(day, timeZone);
      const dayInListingTZ = getStartOf(timeOfDay, 'day', timeZone);
      const lastDayToEndBooking = endDateToPickerDate(
        lineItemUnitType,
        endOfBookableRange,
        timeZone
      );
      return (
        !isDateSameOrAfter(dayInListingTZ, startOfStartDay) ||
        !isDateSameOrAfter(lastDayToEndBooking, dayInListingTZ)
      );
    };
  }

  // end date selected, start date missing
  // -> limit the earliest start date for the booking so that it
  // needs to be after the previous booked date
  const endDateSelected = monthlyTimeSlots && endDate && !startDate && focusedInput !== END_DATE;
  const previousBookedDate = endDateSelected
    ? lastBlockedBetween(monthlyTimeSlots, TODAY, endDate, timeZone)
    : null;

  if (previousBookedDate) {
    return day => {
      const timeOfDay = timeOfDayFromLocalToTimeZone(day, timeZone);
      const dayInListingTZ = getStartOf(timeOfDay, 'day', timeZone);
      const firstDayToStartBooking = getStartOf(previousBookedDate, 'day', timeZone, 1, 'days');
      return (
        !isDateSameOrAfter(dayInListingTZ, firstDayToStartBooking) ||
        !isDateSameOrAfter(endOfAvailableRangeDate, dayInListingTZ)
      );
    };
  }

  // standard isOutsideRange function
  return day => {
    const timeOfDay = timeOfDayFromLocalToTimeZone(day, timeZone);
    const dayInListingTZ = getStartOf(timeOfDay, 'day', timeZone);
    return (
      !isDateSameOrAfter(dayInListingTZ, TODAY) ||
      !isDateSameOrAfter(endOfAvailableRangeDate, dayInListingTZ)
    );
  };
};

// Checks if time slot (propTypes.timeSlot) start time equals a day (moment)
// eslint-disable-next-line no-unused-vars
const timeSlotEqualsDay = (timeSlot, day, timeZone) => {
  const isTimeBased = timeSlot?.attributes?.type === TIME_SLOT_TIME;
  return isTimeBased
    ? isInRange(day, timeSlot.attributes.start, timeSlot.attributes.end, undefined, timeZone)
    : false;
};

/**
 * Returns an isDayBlocked function that can be passed to
 * a react-dates DateRangePicker component.
 */
const isDayBlockedFn = ({
  monthlyTimeSlots,
  startDate,
  endDate,
  unitType,
  dayCountAvailableForBooking,
  timeZone,
  listing,
}) => focusedInput => {
  const endOfAvailableRange = dayCountAvailableForBooking - 1;
  const outOfBookableDate = getStartOf(TODAY, 'day', timeZone, endOfAvailableRange + 1, 'days');
  const timeSlotsOnSelectedMonth = pickMonthlyTimeSlots(monthlyTimeSlots, startDate, timeZone);

  // Start date selected/focused, end date missing
  const startDateSelected = isStartDateSelected(
    timeSlotsOnSelectedMonth,
    startDate,
    endDate,
    focusedInput
  );

  // Find the end of bookable range after a start date
  const endOfBookableRange = startDateSelected
    ? firstBlockedBetween(monthlyTimeSlots, startDate, outOfBookableDate, timeZone)
    : null;

  // end date is focused but no dates are selected
  const selectingEndDate =
    timeSlotsOnSelectedMonth &&
    !startDate &&
    !endDate &&
    focusedInput === END_DATE &&
    unitType === LINE_ITEM_NIGHT;

  if (selectingEndDate) {
    // if end date is being selected first, block the day after a booked date
    // (as a booking can end on the day the following booking starts)
    return day => {
      const timeOfDay = timeOfDayFromLocalToTimeZone(day, timeZone);
      const dayInListingTZ = getStartOf(timeOfDay, 'day', timeZone);
      const timeslots = Object.values(monthlyTimeSlots)
        .map(item => item.timeSlots)
        .flat();
      const available = getAvailabilityForDayWithBuffer({
        timeslots,
        dayInListingTZ,
        listing,
        requiredSeats: 1,
      });
      return !available;
    };
  } else if (endOfBookableRange || !timeSlotsOnSelectedMonth) {
    // a next bookable range is found or time slots are not provided
    // -> booking range handles blocking dates
    return () => false;
  } else {
    // otherwise return standard timeslots check
    return day => {
      const timeOfDay = timeOfDayFromLocalToTimeZone(day, timeZone);
      const dayInListingTZ = getStartOf(timeOfDay, 'day', timeZone);
      const timeslots = Object.values(monthlyTimeSlots)
        .map(item => item.timeSlots || [])
        .flat();
      const available = getAvailabilityForDayWithBuffer({
        timeslots,
        dayInListingTZ,
        listing,
        requiredSeats: 1,
      });
      return !available;
    };
  }
};

const fetchMonthData = (
  date,
  listingId,
  dayCountAvailableForBooking,
  timeZone,
  onFetchTimeSlots
) => {
  const endOfRangeDate = endOfRange(TODAY, dayCountAvailableForBooking, timeZone);

  // Don't fetch timeSlots for past months or too far in the future
  if (isInRange(date, TODAY, endOfRangeDate)) {
    // Use "today", if the first day of given month is in the past
    const start = isDateSameOrAfter(TODAY, date) ? TODAY : date;

    // Use endOfRangeDate, if the first day of the next month is too far in the future
    const nextMonthDate = nextMonthFn(date, timeZone);
    const end = isDateSameOrAfter(nextMonthDate, endOfRangeDate)
      ? getStartOf(endOfRangeDate, 'day', timeZone)
      : nextMonthDate;

    // Fetch time slots for given time range
    onFetchTimeSlots(listingId, start, end, timeZone);
  }
};

const handleMonthClick = (
  currentMonth,
  monthlyTimeSlots,
  fetchMonthData,
  dayCountAvailableForBooking,
  timeZone,
  listingId,
  onFetchTimeSlots
) => monthFn => {
  // Callback function after month has been updated.
  // react-dates component has next and previous months ready (but inivisible).
  // we try to populate those invisible months before user advances there.
  fetchMonthData(
    monthFn(currentMonth, timeZone, 2),
    listingId,
    dayCountAvailableForBooking,
    timeZone,
    onFetchTimeSlots
  );

  // If previous fetch for month data failed, try again.
  const monthId = monthIdString(currentMonth, timeZone);
  const currentMonthData = monthlyTimeSlots[monthId];
  if (currentMonthData && currentMonthData.fetchTimeSlotsError) {
    fetchMonthData(
      currentMonth,
      listingId,
      dayCountAvailableForBooking,
      timeZone,
      onFetchTimeSlots
    );
  }
};

// In case start or end date for the booking is missing
// focus on that input, otherwise continue with the
// default handleSubmit function.
const handleFormSubmit = (setFocusedInput, onSubmit) => values => {
  const { startDate, endDate } = values.bookingDates || {};
  if (!startDate) {
    setFocusedInput(START_DATE);
    return { [FORM_ERROR]: 'required' };
  } else if (!endDate) {
    setFocusedInput(END_DATE);
    return { [FORM_ERROR]: 'required' };
  } else {
    onSubmit(values);
  }
};

// Function that can be passed to nested components
// so that they can notify this component when the
// focused input changes.
const handleFocusedInputChange = setFocusedInput => focusedInput => {
  setFocusedInput(focusedInput);
};

// When the values of the form are updated we need to fetch
// lineItems from this Template's backend for the EstimatedTransactionMaybe
// In case you add more fields to the form, make sure you add
// the values here to the orderData object.
const handleFormSpyChange = ({
  listingId,
  isOwnListing,
  fetchLineItemsInProgress,
  onFetchTransactionLineItems,
  monthlyTimeSlots,
  setInsufficientSeats,
  timeZone,
  search,
  currentMonth,
  setNotPossibleToDeliverToCountry,
  listing,
  authorizedUser,
  handleSyncDates,
}) => formValues => {
  const { seats, bookingDates = {}, deliveryAddress, deliveryMethod } = formValues.values || {};
  const { startDate, endDate } = bookingDates;
  const timeslots = Object.values(monthlyTimeSlots)
    .map(item => item.timeSlots || [])
    .flat();
  const sufficientSeats = getSufficientSeats({
    startDate,
    endDate,
    seats,
    timeslots,
  });
  const country = deliveryAddress?.value?.countryCode || parseSearchCountry({ search });
  const { country: listingCountry = [], minimalRentalPeriod = 0 } =
    listing?.attributes?.publicData || {};
  const countrySupported = listingCountry.includes(country);
  const deliveryPossible =
    authorizedUser && deliveryMethod === 'shipping' ? country && countrySupported : true;

  if (country) {
    setNotPossibleToDeliverToCountry(!deliveryPossible);
  }

  if (!formValues.active) {
    // This breaks the date sync with the search bar
    return;
  }

  setInsufficientSeats(!sufficientSeats);

  const period = diffInTime(endDate, startDate, 'days', true);
  if (startDate && endDate && period >= minimalRentalPeriod && !formValues.active) {
    handleSyncDates(bookingDates);
  }

  if (
    sufficientSeats &&
    startDate &&
    endDate &&
    seats &&
    !fetchLineItemsInProgress &&
    deliveryPossible
  ) {
    onFetchTransactionLineItems({
      orderData: {
        bookingStart: startDate,
        bookingEnd: endDate,
        seats: Number(seats),
        country,
        deliveryMethod,
      },
      listingId,
      isOwnListing,
    });
  }
};

const handleActionButtonClick = ({ form, setFocusedInput, setFocusedAddress }) => e => {
  const { seats, bookingDates = {}, deliveryAddress, deliveryMethod } =
    form.getState().values || {};
  const { startDate, endDate } = bookingDates;

  if (!startDate) {
    e.preventDefault();
    setFocusedInput(START_DATE);
  } else if (!endDate) {
    e.preventDefault();
    setFocusedInput(END_DATE);
  } else if (!seats || seats < 1) {
    e.preventDefault();
    // Blur event will show validator message
    form.blur('seats');
    form.focus('seats');
  } else if (deliveryMethod === 'shipping' && !deliveryAddress) {
    e.preventDefault();
    setFocusedAddress(true);
  } else {
    return true;
  }

  return false;
};

const handleAddToCart = ({
  listing,
  currentUser,
  form,
  updateCart,
  setCartUpdated,
  setFocusedInput,
  setFocusedAddress,
}) => e => {
  e.preventDefault();
  const { seats, bookingDates = {}, deliveryAddress } = form.getState().values || {};
  const { startDate, endDate } = bookingDates;

  if (handleActionButtonClick({ form, setFocusedInput, setFocusedAddress })(e)) {
    updateCart({
      listing,
      seats,
      startDate,
      endDate,
      action: CART_ADD,
      currentUser,
      addressId: deliveryAddress?.value?.id,
    })
      .then(response => {
        setCartUpdated(true);
      })
      .catch(err => {
        setCartUpdated(false);
      });
  }
};

// IconArrowHead component might not be defined if exposed directly to the file.
// This component is called before IconArrowHead component in components/index.js
const PrevIcon = props => (
  <IconArrowHead {...props} direction="left" rootClassName={css.arrowIcon} />
);
const NextIcon = props => (
  <IconArrowHead {...props} direction="right" rootClassName={css.arrowIcon} />
);

const Next = props => {
  const { currentMonth, dayCountAvailableForBooking, timeZone } = props;
  const nextMonthDate = nextMonthFn(currentMonth, timeZone);

  return isDateSameOrAfter(
    nextMonthDate,
    endOfRange(TODAY, dayCountAvailableForBooking, timeZone)
  ) ? null : (
    <NextIcon />
  );
};
const Prev = props => {
  const { currentMonth, timeZone } = props;
  const prevMonthDate = prevMonthFn(currentMonth, timeZone);
  const currentMonthDate = getStartOf(TODAY, 'month', timeZone);

  return isDateSameOrAfter(prevMonthDate, currentMonthDate) ? <PrevIcon /> : null;
};

const handleGoBack = ({ history }) => () => {
  history.goBack();
};

const renderDayContents = ({ dayToSeats, timeZone }) => day => {
  if (configDefault.listingCalendarDebug) {
    const timeOfDay = timeOfDayFromLocalToTimeZone(day, timeZone);
    const dayInListingTZ = getStartOf(timeOfDay, 'day', timeZone);
    const seats = dayToSeats[moment(dayInListingTZ).format()];
    return (
      <>
        <span className="renderedDay">{day.format('D')}</span>
        <span className={css.seatsBadge}>{seats}</span>
      </>
    );
  }

  return <span className="renderedDay">{day.format('D')}</span>;
};

const handleSyncDates = ({ history, location, initialDates }) => dates => {
  if (JSON.stringify(dates) === JSON.stringify(initialDates)) {
    return;
  }

  const { pathname, search, state } = location;
  const startDateQuery = dates?.startDate ? moment(dates.startDate).format('YYYY-MM-DD') : null;
  const endDateQuery = dates?.endDate
    ? moment(dates.endDate)
      .subtract(1, 'day')
      .format('YYYY-MM-DD')
    : null;
  const dateQuery = startDateQuery && endDateQuery ? `${startDateQuery},${endDateQuery}` : null;

  if (search.includes(dateQuery)) {
    return;
  }

  const searchParams = omit(parse(search), 'dates');
  const searchString = !dates
    ? `?${stringify(search)}`
    : `?dates=${dateQuery}&${stringify(searchParams)}`;
  history.push(`${pathname}${searchString}`, state);
};

export const BookingDatesFormComponent = props => {
  const {
    rootClassName,
    className,
    price: unitPrice,
    dayPriceTiers,
    listingId,
    isOwnListing,
    fetchLineItemsInProgress,
    onFetchTransactionLineItems,
    onSubmit,
    timeZone,
    dayCountAvailableForBooking,
    marketplaceName,
    payoutDetailsWarning,
    initialDates,
    timeslotsReady,
    currentUser,
    currentUnitPrice,
    setCurrentUnitPrice,
    isAuthenticated,
    ...rest
  } = props;

  const { publicData } = currentUser?.attributes?.profile ?? {};

  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const t = intl.formatMessage;
  const [focusedInput, setFocusedInput] = useState(null);
  const [cartUpdated, setCartUpdated] = useState(false);
  const [insufficientSeats, setInsufficientSeats] = useState(false);
  const [showDeliveryPopup, setShowDeliveryPopup] = useState(false);
  const [visiblePopups, setVisiblePopups] = useState({
    GeneralTermsPopup: false,
    PaymentTermsPopup: false,
    DeliveryTermsPopup: false,
  });
  const [notPossibleToDeliverToCountry, setNotPossibleToDeliverToCountry] = useState(false);
  const [
    updateCart,
    { isLoading: updateCartInProgress, error: updateCartError },
  ] = useUpdateCartMutation();
  const [focusedAddress, setFocusedAddress] = useState(false);
  const authorizedUser = !!props.currentUser?.id?.uuid;
  const [currentMonth, setCurrentMonth] = useState(
    getStartOf(initialDates.startDate ?? TODAY, 'month', props.timeZone)
  );
  const classes = classNames(rootClassName || css.root, className);
  const onFormSubmit = handleFormSubmit(setFocusedInput, onSubmit);
  const onFocusedInputChange = handleFocusedInputChange(setFocusedInput);
  const onFormSpyChange = handleFormSpyChange({
    listingId,
    isOwnListing,
    fetchLineItemsInProgress,
    onFetchTransactionLineItems,
    monthlyTimeSlots: props.monthlyTimeSlots,
    setInsufficientSeats,
    timeZone,
    search: location.search,
    setNotPossibleToDeliverToCountry,
    listing: props.listing,
    authorizedUser,
    handleSyncDates: handleSyncDates({ history, location, initialDates }),
  });

  const GeneralTermsPopupRef = useRef(null);
  const PaymentTermsPopupRef = useRef(null);
  const DeliveryTermsPopupRef = useRef(null);

  useEffect(() => {
    setCurrentMonth(getStartOf(initialDates.startDate ?? TODAY, 'month', props.timeZone));

    calculateUnitPrice(initialDates.startDate, initialDates.endDate);
  }, [initialDates]);

  useEffect(() => {
    if (focusedAddress) {
      setFocusedAddress(false);
    }
  }, [focusedAddress]);

  const handleClickOutside = event => {
    if (
      (GeneralTermsPopupRef.current && !GeneralTermsPopupRef.current.contains(event.target)) ||
      (PaymentTermsPopupRef.current && !PaymentTermsPopupRef.current.contains(event.target)) ||
      (DeliveryTermsPopupRef.current && !DeliveryTermsPopupRef.current.contains(event.target))
    ) {
      setVisiblePopups({
        GeneralTermsPopup: false,
        PaymentTermsPopup: false,
        DeliveryTermsPopup: false,
      });
    }
  };

  useEffect(() => {
    if (
      visiblePopups.GeneralTermsPopup ||
      visiblePopups.PaymentTermsPopup ||
      visiblePopups.DeliveryTermsPopup
    ) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [visiblePopups]);

  const { deliveryOptions = [], minimalRentalPeriod = 0 } =
    props.listing?.attributes?.publicData || {};
  // const deliveryMethod = authorizedUser
  //   ? deliveryOptions.length > 1
  //     ? 'shipping'
  //     : deliveryOptions[0]
  //   : 'pickup';
  const deliveryMethod = authorizedUser ? 'shipping' : 'pickup';

  const allTimeslots = Object.values(props.monthlyTimeSlots)
    .map(item => item.timeSlots)
    .flat();
  const dayToSeats = createDayToSeats(allTimeslots);

  const shippingClickHandler = () => {
    setVisiblePopups({ DeliveryTermsPopup: true });
  };

  const calculateUnitPrice = (startDate, endDate) => {
    const dayDiff =
      startDate && endDate ? parseInt(moment(endDate).diff(moment(startDate), 'day')) : 1;
    const selectedUnitPriceRaw =
      dayPriceTiers?.find?.(
        priceTier => dayDiff >= priceTier.firstItem && dayDiff <= priceTier.lastItem
      ) ?? dayPriceTiers?.[0];

    const selectedUnitPrice = selectedUnitPriceRaw
      ? new Money(selectedUnitPriceRaw.price.amount, selectedUnitPriceRaw.price.currency)
      : unitPrice;

    setCurrentUnitPrice(selectedUnitPrice);
  };

  useEffect(() => {
    const seats = 1;
    const country = parseSearchCountry({ search: location.search });
    const { startDate, endDate } = initialDates || {};
    const sufficientSeats = getSufficientSeats({
      startDate,
      endDate,
      seats,
      timeslots: allTimeslots,
    });
    const { country: listingCountry = [] } =
      props.listing?.attributes?.publicData || {};
    const countrySupported = listingCountry.includes(country);
    const deliveryPossible =
      authorizedUser && deliveryMethod === 'shipping' ? country && countrySupported : true;

    if (
      sufficientSeats &&
      startDate &&
      endDate &&
      seats &&
      !fetchLineItemsInProgress &&
      deliveryPossible
    ) {
      onFetchTransactionLineItems({
        orderData: {
          bookingStart: startDate,
          bookingEnd: endDate,
          seats: Number(seats),
          country,
          deliveryMethod,
        },
        listingId,
        isOwnListing,
      });
    }
  }, [initialDates, props.monthlyTimeSlots, location.search]);

  return (
    <FinalForm
      {...rest}
      initialValues={{
        deliveryMethod,
        bookingDates: initialDates,
        seats: 1,
      }}
      unitPrice={unitPrice}
      onSubmit={onFormSubmit}
      keepDirtyOnReinitialize={true}
      render={fieldRenderProps => {
        const {
          endDatePlaceholder,
          startDatePlaceholder,
          formId,
          handleSubmit,
          lineItemUnitType,
          values,
          monthlyTimeSlots,
          lineItems,
          fetchLineItemsError,
          onFetchTimeSlots,
          form,
          onManageDisableScrolling,
          listing,
          invalid,
        } = fieldRenderProps;
        const { startDate, endDate } = values && values.bookingDates ? values.bookingDates : {};
        const { deliveryMethod, deliveryAddress } = values || {};
        const selectedCountryCode = deliveryAddress?.value?.countryCode || null;

        const startDateErrorMessage = intl.formatMessage({
          id: 'FieldDateRangeInput.invalidStartDate',
        });
        const endDateErrorMessage = intl.formatMessage({
          id: 'FieldDateRangeInput.invalidEndDate',
        });

        const resetDates = () => {
          setCurrentMonth(getStartOf(startOfToday, 'month', timeZone));
          form.change('bookingDates', { startDate: null, endDate: null });
        };

        // This is the place to collect breakdown estimation data.
        // Note: lineItems are calculated and fetched from this Template's backend
        // so we need to pass only booking data that is needed otherwise
        // If you have added new fields to the form that will affect to pricing,
        // you need to add the values to handleOnChange function
        const breakdownData =
          startDate && endDate
            ? {
              startDate,
              endDate,
            }
            : null;

        const showEstimatedBreakdown =
          isAuthenticated &&
          breakdownData &&
          lineItems &&
          !fetchLineItemsInProgress &&
          !fetchLineItemsError &&
          !insufficientSeats &&
          !notPossibleToDeliverToCountry;

        const dateFormatOptions = {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
        };

        const startOfToday = getStartOf(TODAY, 'day', timeZone);
        const tomorrow = addTime(startOfToday, 1, 'days');
        const startDatePlaceholderText =
          startDatePlaceholder || intl.formatDate(startOfToday, dateFormatOptions);
        const endDatePlaceholderText =
          endDatePlaceholder || intl.formatDate(tomorrow, dateFormatOptions);

        const onMonthClick = handleMonthClick(
          currentMonth,
          monthlyTimeSlots,
          fetchMonthData,
          dayCountAvailableForBooking,
          timeZone,
          listingId,
          onFetchTimeSlots
        );
        const isDayBlocked = isDayBlockedFn({
          monthlyTimeSlots,
          startDate,
          endDate,
          unitType: lineItemUnitType,
          dayCountAvailableForBooking,
          timeZone,
          listing: props.listing,
        });
        const isOutsideRange = isOutsideRangeFn({
          monthlyTimeSlots,
          startDate,
          endDate,
          lineItemUnitType,
          dayCountAvailableForBooking,
          timeZone,
          listing: props.listing,
        });
        const showDeliveryMethodShipping = deliveryOptions.includes('shipping');
        const showDeliveryMethodPickup = deliveryOptions.includes('pickup');

        if (!timeslotsReady) {
          return <IconSpinner />;
        }

        const noTerms = intl.formatMessage({id: "BookingDatesForm.noTermText"})

        const {
          generalTermsConditions = noTerms,
          paymentTerms = noTerms,
          deliveryTerms = noTerms,
        } = rest?.listing?.author?.attributes?.profile?.publicData ?? {};

        return (
          <Form onSubmit={handleSubmit} className={classes} enforcePagePreloadFor="CheckoutPage">
            <FormSpy subscription={{ values: true, active: true }} onChange={onFormSpyChange} />
            <FieldDateRangeInput
              className={css.bookingDates}
              name="bookingDates"
              resetDates={resetDates}
              isDaily={lineItemUnitType === LINE_ITEM_DAY}
              startDateId={`${formId}.bookingStartDate`}
              startDateLabel={intl.formatMessage({
                id: 'BookingDatesForm.bookingStartTitle',
              })}
              startDatePlaceholderText={startDatePlaceholderText}
              endDateId={`${formId}.bookingEndDate`}
              endDateLabel={intl.formatMessage({
                id: 'BookingDatesForm.bookingEndTitle',
              })}
              endDatePlaceholderText={endDatePlaceholderText}
              focusedInput={focusedInput}
              onFocusedInputChange={onFocusedInputChange}
              format={v => {
                const { startDate, endDate } = v || {};
                // Format the Final Form field's value for the DateRangeInput
                // DateRangeInput operates on local time zone, but the form uses listing's time zone
                const formattedStart = startDate
                  ? timeOfDayFromTimeZoneToLocal(startDate, timeZone)
                  : startDate;
                const formattedEnd = endDate
                  ? timeOfDayFromTimeZoneToLocal(endDate, timeZone)
                  : endDate;
                return v ? { startDate: formattedStart, endDate: formattedEnd } : v;
              }}
              parse={v => {
                const { startDate, endDate } = v || {};
                // Parse the DateRangeInput's value (local noon) for the Final Form
                // The form expects listing's time zone and start of day aka 00:00
                const parsedStart = startDate
                  ? getStartOf(timeOfDayFromLocalToTimeZone(startDate, timeZone), 'day', timeZone)
                  : startDate;
                const parsedEnd = endDate
                  ? getStartOf(timeOfDayFromLocalToTimeZone(endDate, timeZone), 'day', timeZone)
                  : endDate;
                return v ? { startDate: parsedStart, endDate: parsedEnd } : v;
              }}
              useMobileMargins
              startDate={startDate}
              endDate={endDate}
              onChangeHelper={({ startDate, endDate }) => {
                calculateUnitPrice(startDate, endDate);
              }}
              validate={composeValidators(
                // required(
                //   intl.formatMessage({
                //     id: 'BookingDatesForm.requiredDate',
                //   })
                // ),
                // bookingDatesRequired(startDateErrorMessage, endDateErrorMessage),
                minimalRentalPeriodRequired(
                  intl.formatMessage(
                    {
                      id: 'BookingDatesForm.requiredMinimalRentalPeriod',
                    },
                    {
                      minimalRentalPeriod,
                    }
                  ),
                  minimalRentalPeriod
                )
              )}
              initialVisibleMonth={initialVisibleMonth(startDate || startOfToday, timeZone)}
              navNext={
                <Next
                  currentMonth={currentMonth}
                  timeZone={timeZone}
                  dayCountAvailableForBooking={dayCountAvailableForBooking}
                />
              }
              navPrev={<Prev currentMonth={currentMonth} timeZone={timeZone} />}
              onPrevMonthClick={() => {
                setCurrentMonth(prevMonth => prevMonthFn(prevMonth, timeZone));
                onMonthClick(prevMonthFn);
              }}
              onNextMonthClick={() => {
                setCurrentMonth(prevMonth => nextMonthFn(prevMonth, timeZone));
                onMonthClick(nextMonthFn);
              }}
              isDayBlocked={isDayBlocked}
              isOutsideRange={isOutsideRange}
              isBlockedBetween={isBlockedBetween(monthlyTimeSlots, timeZone)}
              disabled={fetchLineItemsInProgress}
              onClose={({ startDate, endDate }) => {
                setCurrentMonth(getStartOf(startDate ?? startOfToday, 'month', timeZone));
              }}
              renderDayContents={renderDayContents({ dayToSeats, timeZone })}
            />

            {/* <FieldTextInput
              className={css.seatsField}
              id={`${formId}.seatsField`}
              name="seats"
              type="number"
              label={t({ id: 'BookingDatesForm.seatsLabel' })}
              min={1}
              validate={numberAtLeast(
                t({
                  id: 'BookingDatesForm.seatsRequired',
                }),
                1
              )}
            /> */}
            <div className={css.seatsField}>
              <label className={css.seatsInputLabel}>
                <FormattedMessage id="BookingDatesForm.seatsLabel" />
              </label>
              <div className={css.seatsInputWrapper}>
                <button
                  className={css.seatsInputBtn}
                  type="button"
                  onClick={() => {
                    if (values.seats > 1) {
                      form.focus('seats');
                      form.change('seats', values.seats - 1);
                      form.blur('seats');
                    }
                  }}
                >
                  <FontAwesomeIcon className={css.seatsInputBtnIcon} icon={faMinus} />
                </button>
                <FieldTextInput
                  name="seats"
                  className={css.seatInputBtnValue}
                  validate={numberAtLeast(
                    t({
                      id: 'BookingDatesForm.seatsRequired',
                    }),
                    1
                  )}
                  disabled
                />
                <button
                  className={css.seatsInputBtn}
                  type="button"
                  onClick={() => {
                    form.focus('seats');
                    form.change('seats', values.seats + 1);
                    form.blur('seats');
                  }}
                >
                  <FontAwesomeIcon className={css.seatsInputBtnIcon} icon={faPlus} />
                </button>
              </div>
            </div>
            {authorizedUser && (
              <>
                {/* <div className={css.deliveryMethods}>
                  {showDeliveryMethodShipping && (
                    <BookingDeliveryRadioButtonComponent
                      id={`${formId}.deliveryMethod-shipping`}
                      name="deliveryMethod"
                      label={intl.formatMessage({ id: 'BookingDatesForm.shippingOption' })}
                      value="shipping"
                      showAsRequired={true}
                      values={values}
                    />
                  )}
                  {showDeliveryMethodPickup && (
                    <BookingDeliveryRadioButtonComponent
                      id={`${formId}.deliveryMethod-pickup`}
                      name="deliveryMethod"
                      label={intl.formatMessage({ id: 'BookingDatesForm.pickupOption' })}
                      value="pickup"
                      showAsRequired={true}
                      values={values}
                    />
                  )}
                </div> */}
                {/* {deliveryMethod === 'shipping' && ( */}
                <label className={css.shippingLabel}>
                  <FormattedMessage id="BookingDatesForm.shippingLabel" />
                </label>
                <FieldDeliveryAddress
                  name="deliveryAddress"
                  id="deliveryAddress"
                  userType={publicData?.userType}
                  focusedSelect={focusedAddress}
                />
                {/* )} */}
              </>
            )}

            {showEstimatedBreakdown ? (
              <div className={css.priceBreakdownContainer}>
                {/* <H6 as="h3" className={css.bookingBreakdownTitle}>
                  <FormattedMessage id="BookingDatesForm.priceBreakdownTitle" />
                </H6> */}
                {/* <hr className={css.totalDivider} /> */}
                <EstimatedCustomerBreakdownMaybe
                  breakdownData={breakdownData}
                  lineItems={lineItems}
                  timeZone={timeZone}
                  currency={currentUnitPrice.currency}
                  marketplaceName={marketplaceName}
                  processName={BOOKING_PROCESS_NAME}
                  shippingClickHandler={shippingClickHandler}
                />
                {visiblePopups.DeliveryTermsPopup && (
                  <div ref={DeliveryTermsPopupRef} className={css.deliveryPopupContainer}>
                    <div className={css.deliveryPopupHeader}>
                      <FontAwesomeIcon
                        icon={faXmark}
                        className={css.deliveryPopupCloseIcon}
                        onClick={() => setVisiblePopups({ DeliveryTermsPopup: false })}
                      />
                      <div>
                        <FormattedMessage id="BookingDatesForm.deliveryTerms.popup.title" />
                      </div>
                    </div>
                    <div className={css.deliveryPopupContent}>
                      <ReactMarkdown>{deliveryTerms}</ReactMarkdown>
                    </div>
                  </div>
                )}
              </div>
            ) : isAuthenticated ? (
              <div className={css.placeHolderPriceWrapper}>
                <hr className={css.totalDivider} />
                <LineItemBasePriceMaybe
                  lineItems={[
                    {
                      code: 'line-item/day',
                      unitPrice: currentUnitPrice,
                      lineTotal: (() => {
                        const unitPriceCopy = Object.create(currentUnitPrice);
                        unitPriceCopy._sdkType = currentUnitPrice._sdkType;
                        unitPriceCopy.currency = currentUnitPrice.currency;
                        unitPriceCopy.amount = unitPriceCopy.amount * values.seats;
                        if (startDate && endDate) {
                          unitPriceCopy.amount =
                            unitPriceCopy.amount *
                            parseInt(moment(endDate).diff(moment(startDate), 'day'));
                        }
                        return unitPriceCopy;
                      })(),
                      includeFor: ['customer', 'provider'],
                      seats: values.seats,
                      units:
                        startDate && endDate
                          ? parseInt(moment(endDate).diff(moment(startDate), 'day'))
                          : 1,
                      quantity: null,
                      percentage: null,
                      reversal: false,
                    },
                  ]}
                  code={'line-item/day'}
                  intl={intl}
                />
                {/* {!notPossibleToDeliverToCountry && deliveryAddress && (
                  <LineItemShippingFeeMaybe
                    lineItems={[
                      {
                        code: 'line-item/shipping-fee',
                        unitPrice: unitPrice,
                        lineTotal:
                          startDate && endDate
                            ? (() => {
                                const unitPriceCopy = Object.create(unitPrice);
                                unitPriceCopy._sdkType = unitPrice._sdkType;
                                unitPriceCopy.currency = unitPrice.currency;
                                unitPriceCopy.amount =
                                  parseInt(moment(endDate).diff(moment(startDate), 'day')) *
                                  unitPriceCopy.amount;
                                return unitPriceCopy;
                              })()
                            : unitPrice,
                        includeFor: ['customer', 'provider'],
                        seats: values.seats,
                        units:
                          startDate && endDate
                            ? parseInt(moment(endDate).diff(moment(startDate), 'day'))
                            : 1,
                        quantity: null,
                        percentage: null,
                        reversal: false,
                      },
                    ]}
                    intl={intl}
                  />
                )} */}
                <LineItemTotalPrice
                  transaction={estimatedCustomerTransaction(
                    [
                      {
                        code: 'line-item/day',
                        unitPrice: currentUnitPrice,
                        lineTotal: (() => {
                          const unitPriceCopy = Object.create(currentUnitPrice);
                          unitPriceCopy._sdkType = currentUnitPrice._sdkType;
                          unitPriceCopy.currency = currentUnitPrice.currency;
                          unitPriceCopy.amount = unitPriceCopy.amount * values.seats;
                          if (startDate && endDate) {
                            unitPriceCopy.amount =
                              unitPriceCopy.amount *
                              parseInt(moment(endDate).diff(moment(startDate), 'day'));
                          }
                          return unitPriceCopy;
                        })(),
                        includeFor: ['customer', 'provider'],
                        seats: values.seats,
                        units:
                          startDate && endDate
                            ? parseInt(moment(endDate).diff(moment(startDate), 'day'))
                            : 1,
                        quantity: null,
                        percentage: null,
                        reversal: false,
                      },
                    ],
                    moment(),
                    moment().add(1, 'day'),
                    unitPrice,
                    timeZone,
                    getProcess(BOOKING_PROCESS_NAME),
                    BOOKING_PROCESS_NAME,
                    unitPrice.currency
                  )}
                  isProvider={false}
                  intl={intl}
                />
              </div>
            ) : null}
            {selectedCountryCode &&
              notPossibleToDeliverToCountry &&
              publicData?.userType !== 'lessor' && (
                <div className={css.notPossibleToDeliverError}>
                  <FormattedMessage id="BookingDatesForm.notPossibleToDeliverToCountry" />
                </div>
              )}

            {fetchLineItemsError ? (
              <span className={css.sideBarError}>
                <FormattedMessage id="BookingDatesForm.fetchLineItemsError" />
              </span>
            ) : null}

            {updateCartError && publicData?.userType !== 'lessor' && (
              <div className={css.seatsError}>
                <FormattedMessage id="BookingDatesForm.addToCartError" />
              </div>
            )}

            {insufficientSeats && publicData?.userType !== 'lessor' && (
              <div className={css.seatsError}>
                <FormattedMessage id="BookingDatesForm.insufficientSeats" />
              </div>
            )}

            {diffInTime(endDate, startDate, 'days', true) < minimalRentalPeriod &&
              publicData?.userType !== 'lessor' && (
                <p className={css.seatsError}>
                  <FormattedMessage
                    id="BookingDatesForm.requiredMinimalRentalPeriod"
                    values={{ minimalRentalPeriod }}
                  />
                </p>
              )}

            {publicData?.userType === 'lessor' && (
              <div className={css.seatsError}>
                <FormattedMessage id="BookingDatesForm.userTypeNotAllowed" />
              </div>
            )}

            <div className={css.submitButton}>
              {/* <PrimaryButton
                type="submit"
                disabled={invalid || insufficientSeats || notPossibleToDeliverToCountry}
                inProgress={fetchLineItemsInProgress}
                onClick={handleActionButtonClick({ form, setFocusedInput, setFocusedAddress })}
              >
                <FormattedMessage
                  id={
                    authorizedUser
                      ? 'BookingDatesForm.requestToBook'
                      : 'BookingDatesForm.chooseLabel'
                  }
                />
              </PrimaryButton> */}
              <div className={css.totalPriceBtn}>
                {(() => {
                  /*       const unitPriceCopy = Object.create(unitPrice);
                  unitPriceCopy._sdkType = unitPrice._sdkType;
                  unitPriceCopy.currency = unitPrice.currency;
                  unitPriceCopy.amount = unitPriceCopy.amount * values.seats; */

                  const unitPriceCopy = Object.create(currentUnitPrice);
                  unitPriceCopy._sdkType = currentUnitPrice._sdkType;
                  unitPriceCopy.currency = currentUnitPrice.currency;
                  unitPriceCopy.amount = unitPriceCopy.amount * values.seats;

                  if (startDate && endDate) {
                    unitPriceCopy.amount =
                      unitPriceCopy.amount *
                      parseInt(moment(endDate).diff(moment(startDate), 'day'));
                  }
                  return isAuthenticated ? formatMoney(intl, unitPriceCopy) : "";
                })()}
              </div>
              <PrimaryButton
                className={css.addToCartButton}
                inProgress={!!updateCartInProgress}
                disabled={
                  invalid ||
                  insufficientSeats ||
                  updateCartInProgress ||
                  notPossibleToDeliverToCountry ||
                  publicData?.userType === 'lessor'
                }
                onClick={
                  authorizedUser
                    ? handleAddToCart({
                      listing,
                      currentUser,
                      form,
                      updateCart,
                      setCartUpdated,
                      setFocusedInput,
                      setFocusedAddress,
                    })
                    : () => {
                      history.push('/login');
                    }
                }
              >
                <FormattedMessage id="BookingDatesForm.addToCartButton" />
              </PrimaryButton>
            </div>
            <div className={css.generalTermsText}>
              {intl.formatMessage(
                { id: 'BookingDatesForm.generalPaymentTermsText' },
                {
                  generalTerms: (
                    <span onClick={() => setVisiblePopups({ GeneralTermsPopup: true })}>
                      {intl.formatMessage({ id: 'BookingDatesForm.generalTermsText' })}
                    </span>
                  ),
                  paymentTerms: (
                    <span onClick={() => setVisiblePopups({ PaymentTermsPopup: true })}>
                      {intl.formatMessage({ id: 'BookingDatesForm.paymentTermsText' })}
                    </span>
                  ),
                }
              )}
              <Modal
                id="AuthenticationPage.tos"
                isOpen={visiblePopups.GeneralTermsPopup}
                onClose={() => setVisiblePopups({ GeneralTermsPopup: false })}
                usePortal
                onManageDisableScrolling={onManageDisableScrolling}
              >
                <div className={css.termsWrapper}>
                  <div className={css.termsHeading}>
                    {intl.formatMessage({ id: 'BookingDatesForm.Popup.TermsAndConditions' })}
                  </div>
                  <div className={css.termsContent}>
                    <ReactMarkdown>{generalTermsConditions}</ReactMarkdown>
                  </div>
                </div>
              </Modal>
              <Modal
                id="AuthenticationPage.tos"
                isOpen={visiblePopups.PaymentTermsPopup}
                onClose={() => setVisiblePopups({ PaymentTermsPopup: false })}
                usePortal
                onManageDisableScrolling={onManageDisableScrolling}
              >
                <div className={css.termsWrapper}>
                  <div className={css.termsHeading}>
                    {intl.formatMessage({ id: 'BookingDatesForm.Popup.PaymentConditions' })}
                  </div>
                  <div className={css.termsContent}>
                    <ReactMarkdown>{paymentTerms}</ReactMarkdown>
                  </div>
                </div>
              </Modal>
            </div>
            {/* {(payoutDetailsWarning || isOwnListing) && (
              <p className={css.finePrint}>
                {payoutDetailsWarning ? (
                  payoutDetailsWarning
                ) : isOwnListing ? (
                  <FormattedMessage id={'BookingDatesForm.ownListing'} />
                ) : (
                  <></>
                )}
              </p>
            )} */}
            <Modal
              id="AddedToCartModal"
              extraContainerClassName={css.ModalContainer}
              contentClassName={css.addedCartModalContent}
              isOpen={cartUpdated}
              onClose={() => setCartUpdated(false)}
              onManageDisableScrolling={onManageDisableScrolling}
              usePortal
            >
              <H4 className={css.modalContainerHeading}>
                <FormattedMessage id="BookingDatesForm.addedCartHeading" />
              </H4>
              <div className={css.cartUpdatedButtons}>
                <SecondaryButton
                  className={css.cartUpdatedBack}
                  onClick={handleGoBack({ history })}
                >
                  <FormattedMessage id="BookingDatesForm.continueShopping" />
                </SecondaryButton>
                <NamedLink name="CartPage">
                  <PrimaryButton type="button" className={css.goToCart}>
                    <FormattedMessage id="BookingDatesForm.toCart" />
                  </PrimaryButton>
                </NamedLink>
              </div>
            </Modal>
          </Form>
        );
      }}
    />
  );
};

BookingDatesFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  price: null,
  isOwnListing: false,
  startDatePlaceholder: null,
  endDatePlaceholder: null,
  lineItems: null,
  fetchLineItemsError: null,
  monthlyTimeSlots: null,
};

BookingDatesFormComponent.propTypes = {
  rootClassName: string,
  className: string,

  marketplaceName: string.isRequired,
  lineItemUnitType: propTypes.lineItemUnitType.isRequired,
  price: propTypes.money,
  isOwnListing: bool,
  monthlyTimeSlots: object,
  onFetchTimeSlots: func.isRequired,

  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
  isAuthenticated: bool.isRequired,

  // for tests
  startDatePlaceholder: string,
  endDatePlaceholder: string,
  dayCountAvailableForBooking: number.isRequired,
};

const mapStateToProps = state => ({
  currentUser: state.user.currentUser,
  initialDates: state.ListingPage.initialDates,
  timeslotsReady: state.ListingPage.timeslotsReady,
});

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
});

const BookingDatesForm = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(BookingDatesFormComponent);

BookingDatesForm.displayName = 'BookingDatesForm';

export default BookingDatesForm;
